// /admin/:resource/:id — create when :id is "new", edit otherwise.
//
// No `mx-auto max-w-…` wrapper here either; AppLayout's <main> is the container
// (see the note in ResourceList.jsx). `max-w-3xl` is applied to the FORM alone,
// because a 120rem-wide text input is unusable while the page around it should
// still line up with every other screen.

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';

import { apiFetch, ApiError } from '../../api/client.js';
import { useProductRole } from '../../session/SessionProvider.jsx';
import DescriptorForm from '../../components/admin/DescriptorForm.jsx';
import OnShiftPreview from '../../components/admin/OnShiftPreview.jsx';
import QueueMembers from '../../components/admin/QueueMembers.jsx';
import FormFields from '../../components/admin/FormFields.jsx';
import FormConditions from '../../components/admin/FormConditions.jsx';
import FieldFormUsage from '../../components/admin/FieldFormUsage.jsx';
import OrgLocations from '../../components/admin/OrgLocations.jsx';
import OrgDomains from '../../components/admin/OrgDomains.jsx';
import {
  initialValues, changedValues, mapFieldErrors, consoleErrorMessage, canAdminister, deletePrompt,
  canCreateInConsole, uncreatableFieldLabels,
  refusalBanner, formWidthClass,
} from './descriptor-form.js';
import { supportPath } from '../../support-path.js';

export default function ResourceEdit() {
  const { resource, id } = useParams();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const supportRole = useProductRole('support');
  const basePath = `/admin/${encodeURIComponent(resource)}`;

  const [descriptor, setDescriptor] = useState(null);
  const [row, setRow] = useState(null);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  // Fields whose editor cannot currently read what is typed in it. A save while one
  // of these is set would send the last good value under a new one's name, so it is
  // blocked rather than guessed at.
  const [draftErrors, setDraftErrors] = useState({});

  useEffect(() => {
    const controller = new AbortController();
    const calls = [apiFetch('/admin', { signal: controller.signal })];
    // Encoded, not interpolated raw: a `..` segment in the URL bar is resolved by
    // the URL parser before the request is sent, which would rewrite the path.
    if (!isNew) calls.push(apiFetch(`${basePath}/${encodeURIComponent(id)}`, { signal: controller.signal }));
    Promise.all(calls)
      .then(([index, one]) => {
        const found = index.data.find((d) => d.name === resource);
        if (!found) {
          setError(`No admin resource named "${resource}".`);
          return;
        }
        setDescriptor(found);
        setRow(one?.data ?? null);
        setValues(initialValues(found, one?.data ?? null));
      })
      .catch((err) => {
        if (err?.name !== 'AbortError') setError(consoleErrorMessage(err));
      });
    return () => controller.abort();
  }, [resource, id, isNew]);

  const setValue = (key, value) => setValues((prev) => ({ ...prev, [key]: value }));

  const noteDraftError = (key, message) => setDraftErrors((prev) => {
    if (!message) {
      const { [key]: _gone, ...rest } = prev;
      return rest;
    }
    return { ...prev, [key]: message };
  });

  const blockingDrafts = Object.keys(draftErrors);
  const mayWrite = descriptor ? canAdminister(descriptor, supportRole) : false;

  function reportFailure(err) {
    // The contract reports every field problem together (docs/API.md §1.6), and
    // `mapFieldErrors` is what makes sure each one is rendered SOMEWHERE — the
    // admin API also raises problems named `actions[0]`, `(body)` and
    // `(resource)`, none of which is a control on this form.
    if (err instanceof ApiError && Array.isArray(err.details?.fields)) {
      const { fieldErrors: mapped, formErrors: unattached } = mapFieldErrors(
        descriptor, err.details.fields,
      );
      setFieldErrors(mapped);
      // `refusalBanner` owns the headline/list split, because the obvious version
      // double-prints: a queue delete is refused for up to three reasons at once,
      // and headline = first + list = all three showed the first one twice.
      const { headline, list } = refusalBanner(mapped, unattached);
      setFormErrors(list);
      setError(headline ?? consoleErrorMessage(err));
      return;
    }
    setError(consoleErrorMessage(err));
  }

  async function save() {
    setSaving(true);
    setError(null);
    setFieldErrors({});
    setFormErrors([]);
    try {
      const body = changedValues(descriptor, values, row);
      if (!isNew && Object.keys(body).length === 0) {
        setSaving(false);
        navigate(supportPath(`/admin/${resource}`));
        return;
      }
      // apiFetch attaches X-CSRF-Token for every method outside GET/HEAD, so there
      // is no plumbing to add here.
      await apiFetch(isNew ? basePath : `${basePath}/${encodeURIComponent(id)}`, {
        method: isNew ? 'POST' : 'PATCH',
        body,
      });
      navigate(supportPath(`/admin/${resource}`));
    } catch (err) {
      setSaving(false);
      reportFailure(err);
    }
  }

  async function remove() {
    setError(null);
    setFormErrors([]);
    try {
      await apiFetch(`${basePath}/${encodeURIComponent(id)}`, { method: 'DELETE' });
      navigate(supportPath(`/admin/${resource}`));
    } catch (err) {
      // A refusal is information, not a failure: a descriptor that cannot be
      // deleted answers 422 with its reason under `(resource)`, and `mapFieldErrors`
      // routes that to the form banner rather than to a control that does not exist.
      reportFailure(err);
    }
  }

  if (error && !descriptor) {
    return (
      <div className="max-w-3xl">
        <Link to={supportPath('/admin')} className="text-sm text-ink-secondary hover:underline">&larr; Admin</Link>
        <div className="mt-4 rounded-lg border border-line-strong bg-sunken p-4 text-sm text-ink">
          {error}
        </div>
      </div>
    );
  }
  if (!descriptor) return <p className="text-sm text-ink-muted">Loading…</p>;

  const singular = descriptor.label.replace(/s$/, '');
  const { label: deleteLabel, confirm: deleteConfirm } = deletePrompt(descriptor, singular);

  return (
    // Per descriptor, because the macro screen carries a reference panel beside the
    // form and 768px would cost the Body 40% of its width to pay for it.
    <div className={formWidthClass(descriptor)}>
      <Link to={supportPath(`/admin/${resource}`)} className="text-sm text-ink-secondary hover:underline">
        &larr; {descriptor.label}
      </Link>
      <h1 className="mt-2 text-xl font-semibold text-ink">
        {isNew ? `New ${singular}` : `Edit ${singular}`}
      </h1>

      {!mayWrite && (
        <p className="mt-2 text-sm text-ink-secondary">
          Editing {descriptor.label} requires the support {descriptor.requires} role. This is
          read-only for you.
        </p>
      )}

      {/* A resource the console cannot create, because a REQUIRED field is one this
          generic form has no control for — today only a saved report's definition, which
          is authored on /reports/builder. Said here as well as hidden on the list,
          because a bookmarked /new URL reaches this screen directly. The form below is
          still rendered and still refuses to save; what this adds is the reason. */}
      {isNew && !canCreateInConsole(descriptor) && (
        <p className="mt-2 text-sm text-ink-secondary">
          A new {singular.toLowerCase()} cannot be created here: {uncreatableFieldLabels(descriptor)}
          {' '}is not something this screen can author. Build it where it is authored, then
          come back to rename or deactivate it.
          {/* Named, because "where it is authored" is useless on its own. A resource
              literal, following this file's existing `resource === 'queues'` pattern
              rather than inventing a served field for one screen. */}
          {resource === 'saved-reports' && (
            <> <Link to={supportPath('/reports/builder')} className="underline">Open the report builder</Link>.</>
          )}
        </p>
      )}

      {error && (
        <div className="mt-4 rounded-lg border border-line-strong bg-sunken p-4 text-sm text-ink">
          {error}
          {formErrors.length > 0 && (
            <ul className="mt-2 list-disc pl-5">
              {formErrors.map((message) => <li key={message}>{message}</li>)}
            </ul>
          )}
        </div>
      )}

      <DescriptorForm
        key={`${resource}/${id}`}
        descriptor={descriptor}
        values={values}
        setValue={setValue}
        fieldErrors={fieldErrors}
        onDraftError={noteDraftError}
        disabled={!mayWrite}
      />

      <div className="mt-6 flex flex-wrap items-center gap-3">
        {mayWrite && (
          <button
            type="button"
            className="btn-primary disabled:opacity-50"
            disabled={saving || blockingDrafts.length > 0}
            onClick={() => { void save(); }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        )}
        <Link to={supportPath(`/admin/${resource}`)} className="btn-ghost ring-1 ring-line-strong">
          {mayWrite ? 'Cancel' : 'Back'}
        </Link>

        {/* The delete control is rendered from the descriptor's own `can_delete`,
            so a resource that refuses deletion never shows a button whose only
            outcome is a 422 — and from `delete_mode`, so the button says which of
            the two deletes this is. Most descriptors soft-delete: the handler sets
            `softDeleteColumn` to 0 and the row survives. `queues` has no
            `is_active` column at all and really removes the row, and calling that
            "Deactivate" would be a promise the handler does not keep. */}
        {!isNew && mayWrite && descriptor.can_delete && (
          <button
            type="button"
            className="btn-ghost ring-1 ring-line-strong"
            onClick={() => { if (window.confirm(deleteConfirm)) void remove(); }}
          >
            {deleteLabel}
          </button>
        )}
        {!isNew && descriptor.can_delete === false && descriptor.delete_refusal && (
          <p className="text-sm text-ink-muted">{descriptor.delete_refusal}</p>
        )}
      </div>

      {blockingDrafts.length > 0 && (
        <p className="mt-3 text-xs text-red-700 dark:text-red-300">
          Save is unavailable while {blockingDrafts.join(', ')} cannot be read. Fix the
          highlighted field. The value already saved has not been changed.
        </p>
      )}

      {/* Membership is not a descriptor — `queue_members` is a join table with no
          id of its own, so every one of the generic framework's six routes is
          keyed on something it has not got — and it gets its own component under
          the queue it belongs to rather than a screen of its own.

          ABOVE the on-shift preview, because it is the editable half and the
          preview is the consequence: a lead adds somebody here and reads the
          answer below. Both are rendered to everyone who can reach this screen;
          the API is the authorization, and it holds membership at `lead` while the
          `queues` descriptor above requires `admin`. */}
      {resource === 'queues' && !isNew && <QueueMembers queueId={id} />}

      {/* The create screen's shape: which custom fields this form asks for, in what
          order, and which are mandatory ON THIS FORM. Not part of the descriptor form
          above because ticket_form_fields is a join table with columns of its own,
          and the generic handler writes exactly one table. Hidden for a new form for
          the same reason the queue editors are: there is no id to attach a field to. */}
      {resource === 'ticket-forms' && !isNew && <FormFields formId={id} />}
      {/* AFTER the field list, and that order is the workflow: a condition can only
          name fields the form already carries, so an admin adds them first and then
          decides which ones are hidden until asked for. */}
      {resource === 'ticket-forms' && !isNew && <FormConditions formId={id} />}

      {/* The other end of that link. A condition is configured on the form, but an admin
          holding a FIELD still has to be able to ask "is this one hidden until something
          else is answered, and where do I change it?" — and the riskiest edit on this
          screen, changing a dropdown's choices, is exactly the one that can leave a
          condition pointing at an answer that no longer exists. */}
      {resource === 'custom-fields' && !isNew && <FieldFormUsage fieldId={id} />}

      {/* Shared master data, which is why both sit behind the admin gate rather than on
          the Customers tab. A store answers "which site is this" and a domain answers
          "whose mail is this", and every module later integrated with this platform
          reads both. Hidden for a new organization for the same reason the queue
          editors are: there is no id yet to hang a child record on. */}
      {resource === 'organizations' && !isNew && <OrgLocations organizationId={id} />}
      {resource === 'organizations' && !isNew && <OrgDomains organizationId={id} />}

      {/* Read-only, and shown to everyone who can reach this screen — the router
          floor is already `support.lead`. Below the form rather than above it,
          because it describes what the SAVED queue would do right now; the values
          in the form above may be unsaved edits, and the preview does not know
          about them. It is deliberately not rendered for a new queue: there is no
          id to ask about, and a queue with no members yet has nothing to say. */}
      {resource === 'queues' && !isNew && <OnShiftPreview queueId={id} />}
    </div>
  );
}
